import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Invoice, InvoiceDataProps } from 'entities/Invoice';
import { AppError } from 'entities/Errors';

import {
  getInvoice,
  createInvoice,
  extractInvoiceData,
  getInvoiceList,
} from './invoice.action';

interface InvoiceState {
  fetchLoading: boolean;
  createLoading: boolean;
  extractLoading: boolean;
  deleteLoading: boolean;
  refresh: boolean;
  error: AppError | null;
  invoiceList: Invoice[];
  invoice: Invoice | null;
  invoiceData: InvoiceDataProps | null;
}

const initialState = {
  fetchLoading: false,
  extractLoading: false,
  deleteLoading: false,
  createLoading: false,
  refresh: false,
  invoiceList: [],
  invoice: null,
  invoiceData: null,
  error: null,
} as InvoiceState;

export const InvoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    setRefresh: (state, action: PayloadAction<{ refresh: boolean }>) => {
      state.refresh = action.payload.refresh;
      if (action.payload.refresh) {
        state.invoice = null;
        state.invoiceData = null;
        state.error = null;
        state.fetchLoading = false;
        state.deleteLoading = false;
        state.createLoading = false;
        state.extractLoading = false;
        state.refresh = false;
      }
    },
    // Zera o resultado do último upload (dados + erro) sem disparar o refresh
    // da lista de faturas. Usado ao (re)abrir a tela de upload: sem isso o
    // erro anterior persiste no estado e o modal, ao reabrir, mostra o mesmo
    // erro e se fecha antes de exibir a área de envio.
    resetInvoiceUpload: (state) => {
      state.invoiceData = null;
      state.error = null;
      state.extractLoading = false;
    },
  },
  extraReducers(builder) {
    builder
      // Mantém a lista anterior enquanto recarrega (o refresh após mutações
      // passou a rebuscar as faturas): zerar aqui piscava os cards do
      // dashboard em "0 fatura(s)" e o aviso de faturas pendentes.
      .addCase(getInvoiceList.pending, (state) => {
        state.error = null;
      })
      .addCase(getInvoiceList.fulfilled, (state, action) => {
        state.invoiceList = action.payload.data;
      })
      .addCase(getInvoiceList.rejected, (state, action) => {
        state.error = action.payload as AppError;
      })
      .addCase(getInvoice.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.invoice = action.payload.data;
      })
      .addCase(getInvoice.rejected, (state, action) => {
        state.fetchLoading = false;
        state.error = action.payload as AppError;
      });

    builder
      .addCase(createInvoice.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.createLoading = false;
        state.invoice = action.payload.data;
        state.invoiceList = [...state.invoiceList, action.payload.data];
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as AppError;
      });

    builder
      .addCase(extractInvoiceData.pending, (state) => {
        state.extractLoading = true;
        state.error = null;
      })
      .addCase(extractInvoiceData.fulfilled, (state, action) => {
        state.extractLoading = false;
        state.invoiceData = action.payload.data;
      })
      .addCase(extractInvoiceData.rejected, (state, action) => {
        state.extractLoading = false;
        state.error = action.payload as AppError;
      });
  },
});

export const { setRefresh: setInvoiceRefresh, resetInvoiceUpload } =
  InvoiceSlice.actions;

export default InvoiceSlice.reducer;
